import { request } from './request';
import { Environment } from 'src/Environment';
import { parseObjectToQueryParamString } from 'src/helpers/functional-helpers';

export enum AccountType {
  Current = '1-Current',
  Savings = '2-Savings',
  Transmission = '3-Transmission',
}

export enum IdType {
  ForeignPassport = 'FPP',
  SouthAfricanID = 'SID',
  SouthAfricanPassport = 'SPP',
  SouthAfricanBusiness = 'SBR',
}

export enum AccountOwnerKind {
  Company = 'company',
  Individual = 'individual',
}

export type AccountCompanyOwner = {
  kind: AccountOwnerKind.Company;
  companyName: string;
  companyRegistrationNumber: string;
  idType: IdType;
};

type AccountIndividualOwner = {
  kind: AccountOwnerKind.Individual;
  idNumber: string;
  idType: IdType;
  surname: string;
};

export enum BankName {
  firstNationalBank = 'FIRST NATIONAL BANK',
  nedbank = 'NEDBANK',
  standardBankSa = 'STANDARD BANK SA',
  standardBank = 'STANDARD BANK',
  absaBank = 'ABSA BANK',
  capitecBank = 'CAPITEC BANK',
  investec = 'INVESTEC',
  discoveryBank = 'DISCOVERY BANK',
  SASFIN = 'SASFIN',
  africanBank = 'AFRICAN BANK',
  absaBankLimited = 'ABSA BANK LIMITED',
  sasfinBankLimited = 'SASFIN BANK LIMITED',
  investecBank = 'INVESTEC BANK',
  tymeBankLimited = 'TYME BANK LIMITED',
  grindrodBank = 'Grindrod Bank',
  albarakaBank = 'ALBARAKA BANK',
  bankZero = 'BANK ZERO',
  bidvestBank = 'BIDVEST BANK',
  bnpParibas = 'BNP PARIBAS',
  citiBank = 'CITIBANK',
  finBond = 'FINBOND MUTUAL BANK',
  finBondEPE = 'FINBOND EPE',
  groBank = 'GROBANK LTD',
  hbzBank = 'HBZ BANK',
  hsbcBank = 'HSBC BANK',
  habibBank = 'HABIB OVERSEAS BANK',
  ithala = 'ITHALA',
  jpMorgan = 'JP MORGAN CHASE',
  lesothoPostBank = 'LESOTHO POST BANK',
  olympusMobile = 'OLYMPUS MOBILE',
  peoplesBank = 'PEOPLES BANK LTD INC NBS',
  postBank = 'POSTBANK',
  saReserve = 'S.A. RESERVE BANK',
  societeGenerale = 'SOCIETE GENERALE',
  standardChartered = 'STANDARD CHARTERED BANK',
  stateBankIndia = 'STATE BANK OF INDIA',
  uBank = 'UBANK LIMITED',
  vbsMutualBank = 'VBS MUTUAL BANK',
}

export type AccountOwner = AccountIndividualOwner | AccountCompanyOwner;

export enum ValidationResult {
  VALID = 'Y',
  INVALID = 'N',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
}

export async function getIsBankAccountValid(
  accountNumber: string,
  accountType: AccountType,
  bankName: string,
  branchCode: string,
  accountOwner: AccountOwner,
  abortController?: AbortController,
  handleErrorResponse?: (errorMessage: string, errorDetails: any) => ValidationResult,
) {
  const params = {
    accountNumber: accountNumber,
    accountType: accountType,
    bankName: bankName,
    branchCode: branchCode,
    idCompRegNo: '',
    idType: '',
    surnameCompName: '',
  };

  switch (accountOwner.kind) {
    case AccountOwnerKind.Company:
      params.idCompRegNo = accountOwner.companyRegistrationNumber;
      params.surnameCompName = accountOwner.companyName;
      params.idType = accountOwner.idType;
      break;

    case AccountOwnerKind.Individual:
      params.idCompRegNo = accountOwner.idNumber;
      params.idType = accountOwner.idType;
      params.surnameCompName = accountOwner.surname;
      break;
  }

  const res = await request(
    `cloud/api/vetting-manage/validateBankAccount?${parseObjectToQueryParamString(params)}`,
    {
      fetchOptions: {
        method: 'GET',
        signal: abortController?.signal || null,
      },
    },
    Environment.current?.apiHostCloud,
    null,
    false,
    true,
  );

  const isAborted = !!abortController && abortController.signal.aborted;
  if (res?.status === 200) {
    return res.result === ValidationResult.VALID;
  } else if (handleErrorResponse && !isAborted) {
    handleErrorResponse('error response from the validateBankAccount GET endpoint', res);
  }
}
